<template>
  
    <!-- Raiz da aplicação Vuetify -->
    <v-app> 

        <!-- Área principal da aplicação -->
        <v-main> 

            <!-- Barra preta -->
            <v-toolbar
                color="black"
                dense
                flat
                :style="{
                    'border-top-left-radius': '12px',
                    'border-top-right-radius': '8px',
                    'position': 'fixed',
                    'top': '64px',
                    'left': modoBarraLateral ? '70px' : '260px',
                    'width': modoBarraLateral ? 'calc(100% - 70px)' : 'calc(100% - 260px)',
                    'z-index': '999',
                    'transition': 'left 0.3s ease, width 0.3s ease'
                }"

            >         
                <v-toolbar-title class="white--text">Empresas Cadastradas: ({{ qntEmpresas }})</v-toolbar-title> 
                <v-spacer />
                
                <!-- Botão para incluir nova empresa -->
                <v-col cols="auto">
                    <v-tooltip text="Nova Empresa" location="top">
                        <template #activator="{ props }">
                        <v-btn
                            v-bind="props"
                            icon
                            large
                            class="rounded-button"
                            style="background-color: #4CAF50;"
                            @click="criaNovaEmpresa"
                        >
                            <v-icon color="white">mdi-plus</v-icon>
                        </v-btn>
                        </template>
                    </v-tooltip>
                </v-col>
                
            </v-toolbar>

            <!-- Conteúdo com espaçamento -->
            <v-container fluid class="pa-4"> 

                <!-- Card que agrupa busca + tabela -->
                <v-card class="pa-4" style="width: 100%; top: 60px">
            
                    <!-- Campo de busca -->
                    <v-text-field
                        v-model="textoBuscaEmpresa"
                        label="Pesquisar"
                        placeholder="Digite sua busca..."
                        prepend-inner-icon="mdi-magnify"
                        variant="outlined"
                        clearable
                        @keyup.enter="carregaListaEmpresas"
                        @click:clear="carregaListaEmpresas"
                        @update:modelValue="atualizaParamentrosBusca"
                        class="tabela-pequena"
                    />
                                        
                    <!-- Tabela de dados -->
                    <v-data-table-server
                        item-value="idEmpresa"
                        v-model:options="opcoesTabelaEmpresas"
                        :headers="headersEmpresas"
                        :items="listaEmpresas"
                        :loading="carregandoEmpresa"
                        @update:options="carregaListaEmpresas"
                        class="elevation-2 rounded-lg tabela-pequena"
                        density="compact"
                        :items-length="totalEmpresas"
                        :footer-props="{
                            'items-per-page-text': 'Itens por página'
                        }"
                        
                    >                
                        <template v-slot:item.funcoes="{ item }">
                    
                            <!-- Ícone de atalho para editar chamado -->
                            <v-tooltip text="Editar Empresa" location="bottom">

                                <template #activator="{ props }">

                                    <v-icon
                                        v-bind="props"
                                        :color="item.ativo === 1 ? 'green' : 'red'" 
                                        size="24" 
                                        @click="editaDadosEmpresa(item.idEmpresa)"
                                        style="cursor: pointer; margin-right: 8px;"
                                    >
                                    mdi-pencil
                                    </v-icon>
                                
                                </template>
                            
                            </v-tooltip>
                            
                        </template>
                        
                        <template #item.ativo="{ item }">
                            <span :class="item.ativo === 1 ? 'text-green' : 'text-red'">
                            {{ item.ativo === 1 ? 'Ativo' : 'Inativo' }}
                            </span>
                        </template>
                                            
                    </v-data-table-server>
                
                </v-card>
        
            </v-container>
        
        </v-main>

    </v-app>

</template>

<script setup>

    /* DEPENDENCIAS E VARIAVEIS COMUNS */
    import { ref, onMounted, onBeforeUnmount } from 'vue'
    
    // Importa axios para chamadas API
    import axios from 'axios'

    // Importa o componente do editor de texto rico
    import '@vueup/vue-quill/dist/vue-quill.snow.css'

    // Importa o event bus
    import { emitter } from '../utils/eventBus'

    // Importa o componente do formulário de erros
    import { useErroStore } from '@/stores/erroStore'

    // Importa o componente do formulário de login
    import { useLoginStore } from '@/stores/loginStore'

    // Importa useRouter para acessar parâmetros de rota
    import { useRouter } from 'vue-router'

    import { channel } from '@/utils/channel'

    // Carrega o endereço das ap
    const API_BASE_URL = import.meta.env.VITE_API_URL;

    // Importa a store de erros
    const erroStore = useErroStore()

    // Importa a store de chamados
    const loginStore = useLoginStore()

    // Acessa o ID da empresa via query param
    const router = useRouter();

    /* VARIAVEIS DO FORMULÁRIO */
    
    // Estado local para o modo da barra lateral
    const modoBarraLateral = ref(false)
    
    const totalEmpresas = ref(0)
    const carregandoEmpresa = ref(false)    
    const textoBuscaEmpresa = ref('')
    const listaEmpresas = ref([])
    const qntEmpresas = ref(0)
    
    const opcoesTabelaEmpresas = ref({ 
        page: 1,
        itemsPerPage: 10,
        sortBy: ['ativo'],
        sortDesc: [true]
    })

    // Cria os headers da tabela
    const headersEmpresas = [
        { title: 'Editar', value: 'funcoes', sortable: false, width: '10px' },
        { title: 'Código', value: 'idEmpresa', sortable: true, width: '10px' },
        { title: 'Empresa', value: 'nomeFantasia', sortable: true, width: '200px' },
        { title: 'Apelido', value: 'apelido', sortable: true, width: '150px' },
        { title: 'UF', value: 'uf', sortable: true, width: '10px' },
        { title: 'Cidade', value: 'cidade', sortable: true, width: '150px' },
        { title: 'Fone', value: 'fonePrincipal', sortable: true, width: '100px' },
        { title: 'Site', value: 'urlSite', sortable: true, width: '200px' },
        { title: 'Condição', value: 'ativo', sortable: true, width: '100px' }        
    ]

    // Função que será chamada sempre que a tabela mudar de página, ordenação ou filtro
    async function carregaListaEmpresas() {

        // Blindagem contra estados intermediários
        const sortArray = Array.isArray(opcoesTabelaEmpresas.value?.sortBy) 
            ? opcoesTabelaEmpresas.value.sortBy 
            : []
        
        const primeiraOrdenacao = sortArray.length ? sortArray[0] : null

        // // 2) Defaults estáveis (sem jamais acessar [0] de algo indefinido)
        const ordenacaoPor = primeiraOrdenacao?.key ?? 'ativo'
        const direcaoOrdenacao = primeiraOrdenacao?.order ?? 'desc'
        
        // Extrai paginação
        const paginaAtual = opcoesTabelaEmpresas.value.page ?? 1
        const itensPorPagina = opcoesTabelaEmpresas.value.itemsPerPage ?? 10
        
        // Limpa lista de empresas
        listaEmpresas.value = []

        // Reseta valor total de empresas
        totalEmpresas.value = 0

        try{

            // Verifica se já está carregando dados da empresa
            if (carregandoEmpresa.value == false ){

                // Seta carregando
                carregandoEmpresa.value = true

                // Chama a API para carregar a lista de empresas
                const { data } = await axios.get(`${API_BASE_URL}/administracao/carregaListaEmpresas`,{
                    params: { 
                        textoBuscaEmpresa: textoBuscaEmpresa.value,
                        ordenacaoPor: ordenacaoPor || 'nome',  
                        direcaoOrdenacao: direcaoOrdenacao || 'asc',
                        paginaAtual: paginaAtual,
                        itensPorPagina: itensPorPagina
                    },
                    headers: {
                        'Authorization': `Bearer ${loginStore.token}`
                    }
                })

                // Garante que mesmo sem dados retorne um array
                const retorno = data.data || []

                qntEmpresas.value = retorno.length

                // Atualiza a lista de empresas e o total
                listaEmpresas.value = retorno
                totalEmpresas.value = data.total            
            }

        } catch(erro){
            
            // Em caso de erro desliga indicador de carregamento e mostra o erro
            carregandoEmpresa.value = false
            erroStore.exibirErro(erro)    
        
        } finally{
            carregandoEmpresa.value = false
        }
    }

    // Se o campo foi limpo, recarrega a lista de ramos de atividade        
    function atualizaParamentrosBusca(novoValor) {
        if (!novoValor) {
            carregaListaEmpresas()
        }
    }
    
    // Esta função chama o cadastro de empresas para criar uma nova empresa
    function criaNovaEmpresa() {    

        // Cria a rota para o cadastro de empresas com os parâmetros necessários
        const route = router.resolve({
            name: 'CadastroEmpresas',
            query: {
                idEmpresa: 0,
                modoBarraLateral: modoBarraLateral.value
            }
        })

        // Abre a rota em uma nova aba do navegador
        window.open(route.href, '_blank')
    }

    // Esta função chama o cadastro de empresas passando o id da mesma e modoBarraLateral
    function editaDadosEmpresa(idEmpresa) {

        // Cria a rota para o cadastro de empresas com os parâmetros necessários
        const route = router.resolve({
            name: 'CadastroEmpresas',
            query: {
                idEmpresa: idEmpresa,
                modoBarraLateral: modoBarraLateral.value
            }
        })

        // Abre a rota em uma nova aba do navegador
        window.open(route.href, '_blank')
    }

    // Função que lê o tamanho da barra no localStorage e aplica na 
    function ajustaBarraPretaTopo(){
        
        if (localStorage.getItem('tamanhoBarraLaterar') == 'true'){
            modoBarraLateral.value = true
        } else{
            modoBarraLateral.value = false
        }
    }

    // Rotina chamada ao montar o componente
    onMounted(() => {

        // Carrega a lista de empresas ao montar o componente
        channel.onmessage = (event) => {
            if (event.data === 'atualizar-lista') {
                carregaListaEmpresas()
            }
        }

        // Listener para mudanças do drawer
        const manipuladorMudancaTamanho = (data) => {

            // Salva valor emitido pelo botão de minimizar/maximizar a barra lateral
            localStorage.setItem('tamanhoBarraLaterar', data)            
            
            // Ajusta barra preta do topo do formulário
            ajustaBarraPretaTopo()
        }
    
        // Adiciona listener para mudanças do drawer
        emitter.on('mudancaBarraLateral', manipuladorMudancaTamanho)

        // Ajusta a barra preta do topo do fomulário com o valor do localStorage
        ajustaBarraPretaTopo()
    })

    // Rotina chamada antes de desmontar o componente
    onBeforeUnmount(() => {

        // Limpar listener
        emitter.off('mudancaBarraLateral')
    })
    
</script>

<style>
    .tabela-pequena .v-data-table__td {
        font-size: 12px;
    }
  
</style>
